import React, { useEffect, useState } from 'react';
import Loader from 'components/atoms/loader';
import Pagination from 'components/atoms/pagination';
import AiManagementOpenButton from 'components/atoms/ai_management_open_button';
import AiManagementSectionTitle from 'components/atoms/ai_management_section_title';
import AiManagementStatusBadge from 'components/atoms/ai_management_status_badge';
import AiManagementDataCard from 'components/molecules/ai_management_data_card';
import AiManagementDrawer from 'components/molecules/ai_management_drawer';
import AiManagementCompetitorBiddingsMap from 'components/molecules/ai_management_competitor_biddings_map';
import AiManagementCompetitorAnalysis from 'components/molecules/ai_management_competitor_analysis';
import AiManagementCompanyDrawer from 'components/organisms/ai_management_company_drawer';
import AiManagementCompetitorBiddingDrawer from 'components/organisms/ai_management_competitor_bidding_drawer';
import AiManagementCompetitorContractsDrawer from 'components/organisms/ai_management_competitor_contracts_drawer';
import { PossibleCompetitorCompany } from 'hooks/usePossibleCompetitorCompanies';
import { usePossibleCompetitorCompanyBiddings } from 'hooks/usePossibleCompetitorCompanyBiddings';
import { useCompanyLocation } from 'hooks/useCompanyLocation';
import {
  PossibleCompetitorBidding,
  PossibleCompetitorSize,
} from 'services/actions/ai_management_possible_competitors';
import { count, money, sum } from 'services/ai_management_format';
import { biddingStatus, finalPrice } from 'services/ai_management_possible_competitors';
import { maskCnpj } from 'utils/cnpj';
import { Content } from './styles';

interface AiManagementPossibleCompetitorDrawerProps {
  biddingId: number | null;
  itemKey: string;
  itemLabel: string;
  period: number;
  company: PossibleCompetitorCompany;
  onClose: () => void;
}

const PER_PAGE = 5;

const VIEWS = [
  { key: 'list', label: 'Lista', icon: 'notes' },
  { key: 'map', label: 'Mapa', icon: 'place' },
] as const;

const AiManagementPossibleCompetitorDrawer: React.FC<AiManagementPossibleCompetitorDrawerProps> = ({
  biddingId,
  itemKey,
  itemLabel,
  period,
  company,
  onClose,
}) => {
  const { biddings, loading, error } = usePossibleCompetitorCompanyBiddings(
    biddingId,
    itemKey,
    company.cnpj,
    period,
  );
  const location = useCompanyLocation(company.cnpj);
  const [view, setView] = useState<'list' | 'map'>('list');
  const [page, setPage] = useState(1);
  const [showCompany, setShowCompany] = useState(false);
  const [opened, setOpened] = useState<PossibleCompetitorBidding | null>(null);
  const [contractsSize, setContractsSize] = useState<PossibleCompetitorSize | null>(null);
  const cnpj = maskCnpj(company.cnpj);
  const pageBiddings = biddings.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  useEffect(() => setPage(1), [biddings]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (opened) setOpened(null);
      else if (contractsSize) setContractsSize(null);
      else if (showCompany) setShowCompany(false);
      else onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, showCompany, opened, contractsSize]);

  const openBidding = (bidding: PossibleCompetitorBidding) => {
    setShowCompany(false);
    setContractsSize(null);
    setOpened(bidding);
  };

  const openCompany = () => {
    setOpened(null);
    setContractsSize(null);
    setShowCompany(true);
  };

  const openContracts = (size: PossibleCompetitorSize) => {
    setOpened(null);
    setShowCompany(false);
    setContractsSize(size);
  };

  const renderList = () => (
    <>
      <AiManagementDataCard>
        <div className="table-responsive">
          <table className="table bg-white mb-0">
            <thead className="thead-light">
              <tr className="text-nowrap">
                <th scope="col">Licitação</th>
                <th scope="col">Situação</th>
                <th scope="col">Quantidade</th>
                <th scope="col">Preço Inicial</th>
                <th scope="col">Preço Final</th>
                <th scope="col">
                  <span className="sr-only">Detalhes</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {pageBiddings.map((bidding) => {
                const status = biddingStatus(bidding.itens);
                const selected = opened?.bidding_id === bidding.bidding_id;
                return (
                  <tr key={bidding.bidding_id} className={selected ? 'is-selected' : undefined}>
                    <td>
                      <button
                        type="button"
                        className="btn btn-link p-0 border-0 text-left"
                        onClick={() => openBidding(bidding)}
                      >
                        {bidding.edital ?? `Nº ${bidding.bidding_id}`}
                      </button>
                      <div
                        className="bidding-orgao text-muted"
                        title={bidding.orgao ?? bidding.cidade ?? undefined}
                      >
                        {bidding.orgao ?? bidding.cidade ?? '—'}
                      </div>
                    </td>
                    <td>
                      <AiManagementStatusBadge tone={status.tone}>
                        {status.text}
                      </AiManagementStatusBadge>
                    </td>
                    <td className="text-nowrap">
                      {count(sum(bidding.itens.map((row) => row.quantidade)))}
                    </td>
                    <td className="text-nowrap">
                      {money(sum(bidding.itens.map((row) => row.proposta_inicial)))}
                    </td>
                    <td className="text-nowrap">{money(sum(bidding.itens.map(finalPrice)))}</td>
                    <td className="text-right">
                      <AiManagementOpenButton
                        label={`Ver detalhes de ${bidding.edital ?? bidding.bidding_id}`}
                        active={selected}
                        onClick={() => openBidding(bidding)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </AiManagementDataCard>

      {biddings.length > PER_PAGE ? (
        <div className="d-flex justify-content-end mt-3">
          <Pagination
            postsPerPage={PER_PAGE}
            totalPosts={biddings.length}
            currentPage={page}
            paginate={setPage}
            firstAndLastPage
          />
        </div>
      ) : null}
    </>
  );

  const renderBody = () => {
    if (loading) {
      return (
        <div className="py-5">
          <Loader />
        </div>
      );
    }
    if (error) {
      return (
        <div className="alert alert-warning mb-0" role="alert">
          Não foi possível carregar as licitações desta empresa.
        </div>
      );
    }
    if (!biddings.length) {
      return (
        <div className="text-muted">
          Nenhuma licitação desta empresa encontrada para este item no período.
        </div>
      );
    }
    return view === 'list' ? (
      renderList()
    ) : (
      <AiManagementCompetitorBiddingsMap biddings={biddings} onOpen={openBidding} />
    );
  };

  return (
    <>
      <AiManagementDrawer
        title={company.name ?? cnpj}
        subtitle={
          <>
            {cnpj}
            {location ? (
              <>
                <span className="mx-2">•</span>
                {location}
              </>
            ) : null}
            <span className="mx-2">•</span>
            <button type="button" className="btn btn-link p-0 align-baseline" onClick={openCompany}>
              Outras informações
            </button>
          </>
        }
        shifted={showCompany || Boolean(opened) || Boolean(contractsSize)}
        onClose={onClose}
      >
        <Content>
          <AiManagementSectionTitle className="mb-2">
            Licitações relacionadas ao item {itemLabel}.
          </AiManagementSectionTitle>

          <div className="btn-group view-toggle mb-3" role="group" aria-label="Visualização">
            {VIEWS.map((option) => (
              <button
                key={option.key}
                type="button"
                className={`btn d-inline-flex align-items-center ${
                  view === option.key ? 'active' : ''
                }`}
                onClick={() => setView(option.key)}
                aria-pressed={view === option.key}
              >
                <i className="material-icons mr-1">{option.icon}</i>
                {option.label}
              </button>
            ))}
          </div>

          {renderBody()}

          <AiManagementCompetitorAnalysis
            biddingId={biddingId}
            cnpj={company.cnpj}
            className="mt-4"
            onOpenContracts={openContracts}
          />
        </Content>
      </AiManagementDrawer>

      {showCompany ? (
        <AiManagementCompanyDrawer
          cnpj={company.cnpj}
          name={company.name}
          onClose={() => setShowCompany(false)}
        />
      ) : null}

      {contractsSize ? (
        <AiManagementCompetitorContractsDrawer
          biddingId={biddingId}
          cnpj={company.cnpj}
          name={company.name}
          size={contractsSize}
          onClose={() => setContractsSize(null)}
        />
      ) : null}

      {opened ? (
        <AiManagementCompetitorBiddingDrawer
          biddingId={biddingId}
          bidding={opened}
          itemKey={itemKey}
          cnpj={company.cnpj}
          onClose={() => setOpened(null)}
        />
      ) : null}
    </>
  );
};

export default AiManagementPossibleCompetitorDrawer;
